import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import BusinessDashboardMockup from "./BusinessDashboardMockup";
import { ContainerScrollAnimation } from "@/components/ui/container-scroll-animation";
import MobileVideoPlayer from "@/components/MobileVideoPlayer";

const BusinessHeroSection = () => {
  const navigate = useNavigate();

  const handleSeeHowItWorks = () => {
    navigate("/call");
  };

  return (
    <section className="relative pt-20 pb-0 md:pt-32 md:pb-0 px-4 md:px-6 overflow-hidden bg-background">
      {/* Animated green glow effect in center */}
      <motion.div
        className="absolute inset-0 z-0 pointer-events-none"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 40%, transparent 70%)'
        }}
      />

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Badge - Mobile optimized like Agency */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.6,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }}
          whileHover={{ scale: 1.05 }}
          className="flex justify-center mb-3 md:mb-6"
        >
          <motion.span
            className="inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1 md:py-2 rounded-full backdrop-blur-sm bg-emerald-500/10 dark:bg-emerald-500/20 text-primary dark:text-emerald-400 border border-emerald-500/25 dark:border-emerald-400/40 text-[10px] md:text-sm font-medium shadow-lg dark:shadow-emerald-500/10"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <motion.span
              className="w-1 md:w-2 h-1 md:h-2 rounded-full bg-emerald-500 dark:bg-emerald-400"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [1, 0.7, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            AI That Answers Every Call
          </motion.span>
        </motion.div>

        {/* Headline - Mobile optimized like Agency */}
        <motion.h1
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.8,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }}
          className="text-[30px] leading-[1.05] sm:text-[32px] md:text-5xl lg:text-6xl text-center text-foreground mx-auto font-bold md:font-semibold px-2 md:px-2 tracking-tight"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Your customers won't wait.
          </motion.span>
          <br className="hidden sm:block" />
          <span className="sm:whitespace-nowrap">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              They'll call{" "}
            </motion.span>
            <motion.span
              className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent font-bold md:font-semibold"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.5,
                ease: [0.22, 1, 0.36, 1]
              }}
              whileHover={{ scale: 1.05 }}
            >
              someone else
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              .
            </motion.span>
          </span>
        </motion.h1>

        {/* Subheadline - Mobile optimized like Agency */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            delay: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }}
          className="mt-3 md:mt-6 text-[13px] sm:text-sm md:text-lg lg:text-xl text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed md:leading-normal px-3 md:px-2"
        >
          Missed calls. Unanswered texts. Buried DMs.
          <br className="hidden sm:block" />
          Every one is a customer lost. Ayra answers them all automatically.
        </motion.p>

        {/* Buttons - Mobile optimized like Agency */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            delay: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }}
          className="mt-5 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-2.5 md:gap-4 px-2 md:px-2"
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <a href="https://calendly.com/sameer-ayra/30min" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                className="w-full sm:w-auto min-h-[44px] md:min-h-[48px] bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-emerald-500 dark:to-teal-500 text-white shadow-lg shadow-emerald-500/20 dark:shadow-emerald-500/30 px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 text-sm md:text-base font-semibold md:font-medium backdrop-blur-sm transition-all"
              >
                Book a Call
                <motion.span
                  animate={{ x: [0, 4, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <ArrowRight className="ml-2 h-4 w-4" />
                </motion.span>
              </Button>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              size="lg"
              variant="outline"
              onClick={handleSeeHowItWorks}
              className="w-full sm:w-auto min-h-[44px] md:min-h-[48px] px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-6 text-sm md:text-base font-semibold md:font-medium border-2 border-emerald-500/25 dark:border-emerald-400/40 dark:text-emerald-400 dark:bg-emerald-500/5 backdrop-blur-sm transition-all hover:border-emerald-500/50"
            >
              <motion.span
                animate={{ scale: [1, 1.1, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Play className="mr-2 h-4 w-4" />
              </motion.span>
              See How It Works
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Mobile Video - Only visible on mobile */}
      <motion.div
        className="lg:hidden mt-6 w-full px-3 sm:px-4 relative z-0"
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.8,
          delay: 1.1,
          ease: [0.22, 1, 0.36, 1]
        }}
      >
        <MobileVideoPlayer
          src="https://vz-12d0dfb2-d16.b-cdn.net/0e58c8e6-d030-462a-b25f-90ddc7b682f7/playlist.m3u8"
          className="max-w-[440px] mx-auto rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5"
        />
      </motion.div>

      {/* Dashboard Mockup with Container Scroll Animation - Only visible on desktop */}
      <motion.div
        className="hidden lg:block -mt-28 lg:-mt-32 w-full scale-95 lg:scale-100 origin-top relative z-0"
        initial={{ opacity: 0, y: 60, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 1,
          delay: 1.2,
          ease: [0.22, 1, 0.36, 1]
        }}
      >
        <ContainerScrollAnimation
          titleComponent={<></>}
          className="pt-0 h-auto"
          preserveDesign={true}
        >
          <BusinessDashboardMockup />
        </ContainerScrollAnimation>
      </motion.div>
    </section>
  );
};

export default BusinessHeroSection;